/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useCallback, useEffect, useRef, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { darken, lighten, Typography } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { difference, differenceBy, findIndex, intersection, keyBy, keys, map, pullAll } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';

import SiteCard from './SiteCard';
import { useFilterState } from '../../../stores/FilterStateStore';
import { timeRangeFilterId, businessServiceGroupFilterId } from '../../filters';
import { useSidebarState, useSidebarStateActions } from './SidebarStateProvider';
import useDebounced from '../../../utilities/UseDebounced';
import { ScopedPlmContextProvider } from '../../links/PlmContext';

const SIDEBAR_WIDTH = 450;
const MAX_SITES = 200;

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        zIndex: 1050,
        transition: 'opacity 0.1s',
        opacity: 0.85,

        '&:hover': {
            opacity: 1,
        },
    },

    expanded: {
        '& $content': {
            marginRight: 0,
            boxShadow: '-10px 0px 10px rgba(0, 0, 0, 0.2)',
        },
    },

    collapsed: {
        '& $content': {
            marginRight: -SIDEBAR_WIDTH,
            boxShadow: 'none',
        },
    },

    content: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,

        height: '100%',
        width: SIDEBAR_WIDTH,
        maxWidth: SIDEBAR_WIDTH,
        margin: 0,

        borderWidth: '0 0 0 1px',
        borderStyle: 'solid',
        borderColor:
            theme.palette.type === 'light'
                ? darken(theme.palette.background.paper, 0.1)
                : lighten(theme.palette.background.paper, 0.1),

        minWidth: 0,
        background: darken(theme.palette.background.paper, 0.05),
    },

    collapser: {
        position: 'absolute',
        top: 23,
        left: 0,
        width: 23,
        height: 60,
        marginLeft: -23,
        boxShadow: '-2px 0px 4px rgba(0, 0, 0, 0.2)',
        zIndex: 1051,
    },
}));

export default function Sidebar(props) {
    const { sites, onSiteMouseEnter, onSiteMouseLeave, timeRangeSelection } = props;

    const classes = useStyles();
    const [filterState] = useFilterState();
    const bsGroup = filterState?.parameters?.[businessServiceGroupFilterId];
    const timeRange = filterState?.parameters?.[timeRangeFilterId];
    const { isExpanded } = useSidebarState();
    const { collapse, expand } = useSidebarStateActions();
    const toggleCollapse = useCallback(() => (isExpanded ? collapse() : expand()), [collapse, expand, isExpanded]);

    return (
        <div className={clsx(classes.root, classes[isExpanded ? 'expanded' : 'collapsed'])}>
            <SidebarCollapser toggleCollapse={toggleCollapse} isExpanded={isExpanded} className={classes.collapser} />

            <div className={classes.content}>
                {isExpanded && (
                    <SidebarSiteContent
                        sites={sites}
                        timeRange={timeRange}
                        bsGroup={bsGroup}
                        timeRangeSelection={timeRangeSelection}
                        onSiteMouseEnter={onSiteMouseEnter}
                        onSiteMouseLeave={onSiteMouseLeave}
                    />
                )}
            </div>
        </div>
    );
}

const useExpandedStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.alignItemsStretch,
        ...theme.mixins.flex.grow(1),

        height: '100%',
    },

    header: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentCenter,

        padding: theme.spacing(2),
    },

    scroller: {
        ...theme.mixins.flex.grow(1),

        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'auto',
    },
}));

function SidebarSiteContent(props) {
    const { sites, bsGroup, timeRange, timeRangeSelection, onSiteMouseEnter, onSiteMouseLeave } = props;

    const classes = useExpandedStyles();
    const scrollerRef = useRef();

    const { activeSite } = useSidebarState();
    const { expandSite } = useSidebarStateActions();
    const expandSiteDebounced = useDebounced(expandSite, 100);

    const renderSites = useRenderSites(sites, activeSite);

    const totalSites = sites.length;
    const isTruncated = totalSites > MAX_SITES;
    const siteCount = renderSites.length;
    const isEmpty = siteCount === 0;

    useEffect(() => {
        if (activeSite && scrollerRef.current) {
            const index = findIndex(renderSites, (item) => item.site.uuid === activeSite);
            const cardEl = scrollerRef.current.children[index];

            if (cardEl) {
                cardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                const expandFn = () => expandSiteDebounced({ uuid: activeSite });
                const scrollerEl = scrollerRef.current;

                scrollerEl.addEventListener('scroll', expandFn);
                expandFn();

                return () => {
                    expandSiteDebounced.cancel();
                    scrollerEl.removeEventListener('scroll', expandFn);
                };
            }
        }
    }, [activeSite, expandSiteDebounced, renderSites]);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography variant="subtitle2" component="div">
                    {isEmpty && <Trans>No sites visible in map</Trans>}

                    {!isEmpty && isTruncated && (
                        <Trans>
                            Showing Worst {siteCount} of {totalSites} Visible Sites
                        </Trans>
                    )}

                    {!isEmpty && !isTruncated && <Trans>Showing {siteCount} Visible Sites</Trans>}
                </Typography>
            </div>
            <div className={classes.scroller} ref={scrollerRef}>
                {renderSites.map((meta) => (
                    <ScopedPlmContextProvider key={meta.site.uuid} plmId={meta.plmId}>
                        <SiteCard
                            meta={meta}
                            bsGroup={bsGroup}
                            timeRange={timeRange}
                            timeRangeSelection={timeRangeSelection}
                            onMouseEnter={onSiteMouseEnter}
                            onMouseLeave={onSiteMouseLeave}
                        />
                    </ScopedPlmContextProvider>
                ))}
            </div>
        </div>
    );
}

const useCollapsedSidebarStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.justifyContentCenter,
        ...theme.mixins.flex.alignItemsCenter,

        cursor: 'pointer',
        color: theme.palette.text.primary,

        background:
            theme.palette.type === 'light'
                ? darken(theme.palette.background.paper, 0.02)
                : lighten(theme.palette.background.paper, 0.03),

        borderStyle: 'solid',
        borderWidth: '1px 0 1px 1px',
        borderColor:
            theme.palette.type === 'light'
                ? darken(theme.palette.background.paper, 0.2)
                : lighten(theme.palette.background.paper, 0.1),

        '&:hover': {
            background: theme.palette.background.paper,
        },

        '& .MuiSvgIcon-fontSizeSmall': {
            fontSize: '1rem',
        },
    },

    expanded: {
        transform: 'rotate(180deg)',
    },
}));

function SidebarCollapser(props) {
    const { className, toggleCollapse, isExpanded } = props;
    const classes = useCollapsedSidebarStyles();

    const titleText = isExpanded ? t`Collapse the sidebar` : t`Expand the sidebar`;

    return (
        <div className={clsx(classes.root, className)} onClick={toggleCollapse} title={i18n._(titleText)}>
            <ChevronLeft className={clsx(isExpanded && classes.expanded)} fontSize="small" />
        </div>
    );
}

function useRenderSites(sites, activeSite) {
    const [renderSites, setRenderSites] = useState([]);
    const activeSitesRef = useRef([]);

    useEffect(() => {
        if (activeSite && activeSitesRef.current.indexOf(activeSite) === -1) {
            activeSitesRef.current.push(activeSite);
        }

        const siteUuidMap = keyBy(sites, (meta) => meta.site.uuid);
        const siteUuids = keys(siteUuidMap);

        const slicedSites = (sites || []).slice(0, MAX_SITES);
        const slicedUuids = map(slicedSites, (s) => s.site.uuid);

        const missingUuids = differenceBy(activeSitesRef.current, slicedUuids);

        // Remove "Active" UUIDs that are no longer included in the site array (we zoomed in or filtered)
        const invalidUuids = difference(missingUuids, siteUuids);
        const validUuids = intersection(missingUuids, siteUuids);

        // remove invalid UUIDs from our active list
        pullAll(activeSitesRef.current, invalidUuids);

        const missingActiveSites = map(validUuids, (uuid) => siteUuidMap[uuid]);

        setRenderSites([...slicedSites, ...missingActiveSites]);
    }, [sites, activeSite]);

    return renderSites;
}
